/**
 * Spec Phase 6 Slice 6D — PromptBar (NL edit) live.
 *
 * Slice 4D shipped this as a decorative bar; Slice 6D wires it
 * to the natural-language section-edit endpoint.
 *
 * Per Phase 6 brief §8:
 *   - Input + send button live when a non-frozen, non-auto section
 *     is active.
 *   - Suggestion chips (stock 3) populate the input on click.
 *   - During the call: input dims, send shows spinner.
 *   - On success: changes_summary surfaces as a toast; warnings
 *     surface above the bar (set on the store; the parent surface
 *     reads lastNlEditWarnings).
 *   - On Enter: submit (Shift+Enter inserts newline if multiline
 *     was a real textarea — current control is a single-line
 *     input, so Enter just submits).
 */
import React, { useCallback, useState } from 'react';
import { CornerDownLeft, Loader2, Sparkles } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

const STOCK_SUGGESTIONS: string[] = [
  'Use formal EP tone',
  'Add a paragraph for the next undescribed figure',
  'Tighten by 20%',
];

export default function PromptBar(): JSX.Element {
  const sections = useSpecStore((s) => s.sections);
  const activeSectionId = useSpecStore((s) => s.activeSectionId);
  const nlEditStatus = useSpecStore((s) => s.nlEditStatus);
  const lastNlEditWarnings = useSpecStore((s) => s.lastNlEditWarnings);
  const lastNlEditSummary = useSpecStore((s) => s.lastNlEditSummary);
  const applyNlEdit = useSpecStore((s) => s.applyNlEdit);
  const clearNlEditWarnings = useSpecStore((s) => s.clearNlEditWarnings);
  const showToast = useSpecStore((s) => s.showToast);

  const active = activeSectionId
    ? sections.find((s) => s.id === activeSectionId) ?? null
    : null;
  const editable = !!active && !active.is_auto_generated && !active.is_frozen;
  const isPending = nlEditStatus === 'pending';
  const inputDisabled = !editable || isPending;

  const [request, setRequest] = useState('');

  const submit = useCallback(async () => {
    if (!active || !request.trim()) return;
    const outcome = await applyNlEdit({
      sectionId: active.id,
      drafterRequest: request,
    });
    if (outcome.kind === 'ok') {
      // Surface the changes_summary as a toast; warnings render
      // above the bar via lastNlEditWarnings selector.
      const summary = outcome.response.nl_edit_meta.changes_summary;
      const warningCount = outcome.response.nl_edit_meta.warnings.length;
      const summaryText =
        summary || `NL edit applied (${outcome.response.affected_paragraphs.length} paragraph(s) changed).`;
      showToast(
        warningCount > 0
          ? `${summaryText} · ${warningCount} warning(s)`
          : summaryText,
      );
      setRequest('');
    } else if (outcome.kind === 'locked') {
      showToast('Specification locked: drawings are no longer frozen.');
    } else {
      showToast(`NL edit failed: ${outcome.message}`);
    }
  }, [active, request, applyNlEdit, showToast]);

  const onKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (!inputDisabled && request.trim()) {
          void submit();
        }
      }
    },
    [inputDisabled, request, submit],
  );

  return (
    <div className="border-t border-zinc-200 bg-white px-6 py-3">
      {lastNlEditWarnings.length > 0 && (
        <div
          className="mb-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="spec-prompt-warnings"
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="font-medium">
              {lastNlEditWarnings.length} warning
              {lastNlEditWarnings.length === 1 ? '' : 's'} from the last edit
            </span>
            <button
              type="button"
              onClick={clearNlEditWarnings}
              className="text-xs text-amber-700 underline hover:text-amber-900"
              data-testid="spec-prompt-warnings-dismiss"
            >
              Dismiss
            </button>
          </div>
          <ul className="list-disc pl-4">
            {lastNlEditWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="s6-promptbar" data-testid="spec-prompt-bar">
        <Sparkles className="h-4 w-4 s6-promptbar-sparkle" aria-hidden />
        <input
          type="text"
          className="flex-1 bg-transparent text-sm text-zinc-700 placeholder:text-zinc-400 outline-none disabled:opacity-50"
          placeholder={
            editable
              ? 'Tell AI what to change in this section…'
              : 'Select a non-frozen, non-auto section to edit'
          }
          disabled={inputDisabled}
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          onKeyDown={onKey}
          data-testid="spec-prompt-input"
          aria-label="Natural-language section edit"
        />
        <div className="hidden items-center gap-1 md:flex">
          {STOCK_SUGGESTIONS.map((s) => (
            <button
              type="button"
              key={s}
              className="rounded-sm border border-zinc-200 px-2 py-1 text-[11px] text-zinc-500 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={inputDisabled}
              onClick={() => setRequest(s)}
              title={`Use "${s}" as the prompt`}
              data-testid={`spec-prompt-chip-${s.replace(/\W+/g, '-').toLowerCase()}`}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm bg-zinc-900 text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400"
          disabled={inputDisabled || !request.trim()}
          onClick={submit}
          aria-label="Send prompt"
          title={isPending ? 'Applying…' : 'Apply natural-language edit'}
          data-testid="spec-prompt-send"
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <CornerDownLeft className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
      {lastNlEditSummary && lastNlEditWarnings.length === 0 && (
        <div
          className="mt-1 text-[11px] text-zinc-400"
          data-testid="spec-prompt-last-summary"
        >
          Last edit: {lastNlEditSummary}
        </div>
      )}
    </div>
  );
}
